import { forwardRef } from "react";
import { Flex, useFlexAlignment } from "../../layout";
import { Keys, isNumber, mergeProps, useAutoFocusChild, useFocusManager, useFocusScope, useKeyboardNavigation, useMergedRefs, useRovingFocus } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { ToolbarContext } from "./ToolbarContext";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "div";
const NavigationKeyBinding = {
  horizontal: {
    first: [Keys.home],
    last: [Keys.end],
    next: [Keys.arrowRight],
    previous: [Keys.arrowLeft]
  },
  vertical: {
    first: [Keys.home],
    last: [Keys.end],
    next: [Keys.arrowDown],
    previous: [Keys.arrowUp]
  }
};
export function InnerToolbar({
  alignX,
  alignY,
  as = DefaultElement,
  autoFocus,
  children,
  disabled,
  forwardedRef,
  gap = 5,
  orientation = "horizontal",
  wrap = true,
  ...rest
}) {
  const alignXValue = useResponsiveValue(alignX);
  const alignYValue = useResponsiveValue(alignY);
  const orientationValue = useResponsiveValue(orientation);
  const wrapValue = useResponsiveValue(wrap);
  const [focusScope, setFocusRef] = useFocusScope();
  const containerRef = useMergedRefs(setFocusRef, forwardedRef);
  const focusManager = useFocusManager(focusScope);
  useRovingFocus(focusScope);
  useAutoFocusChild(focusManager, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus
  });
  const arrowNavigationProps = useKeyboardNavigation(focusManager, NavigationKeyBinding[orientationValue]);
  const alignProps = useFlexAlignment({
    alignX: alignXValue,
    alignY: orientationValue === "horizontal" ? alignYValue !== null && alignYValue !== void 0 ? alignYValue : "center" : alignYValue,
    orientation: orientationValue
  });
  return /*#__PURE__*/_jsx(Flex, { ...mergeProps(rest, {
      "aria-orientation": orientationValue,
      as,
      gap,
      ref: containerRef,
      role: "toolbar",
      wrap: wrapValue ? "wrap" : undefined
    }, alignProps, arrowNavigationProps),
    children: /*#__PURE__*/_jsx(ToolbarContext.Provider, {
      value: {
        disabled,
        orientation: orientationValue
      },
      children: children
    })
  });
}
InnerToolbar.defaultElement = DefaultElement;
export const Toolbar = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerToolbar, { ...props,
  forwardedRef: ref
}));